import { useState, type ReactNode } from 'react'
import { AlertTriangle, Check, KeyRound, Loader2 } from 'lucide-react'
import { Button } from '../primitives/Button'
import { Hint, Label, TextInput } from '../primitives/Field'

export interface LiteLlmDraft {
  baseUrl: string
  virtualKey: string
  /** Chat model. */
  model: string
  /** Transcription model for dictation; '' turns the Spracheingabe off. */
  transcribeModel: string
}

/**
 * Transcription deployments are named for what they do (whisper-1,
 * gpt-4o-transcribe, azure-stt). Used to pre-select sensible defaults — both
 * dropdowns still offer every model the key may call.
 */
const TRANSCRIBE_HINT = /whisper|transcrib|speech.?to.?text|\bstt\b/i

export interface SetupViewProps {
  /** Pre-fill when changing an existing connection. */
  initial?: Partial<LiteLlmDraft>
  /** Suggested proxy URL when nothing is stored yet. */
  defaultBaseUrl: string
  /**
   * Loads the models the key may call. Rejects with a readable message when the
   * URL or the key is wrong — that message is shown verbatim.
   */
  onLoadModels: (baseUrl: string, virtualKey: string) => Promise<string[]>
  onSave: (draft: LiteLlmDraft) => void
  /** Omitted in the first-run gate — there is nothing to go back to. */
  onCancel?: () => void
  variant?: 'onboarding' | 'edit'
}

type Status = 'idle' | 'loading' | 'ready' | 'error'

/**
 * Connection dialog for the LiteLLM backend. Shown as a gate on first start
 * (nothing is usable without a Virtual Key) and again from the settings when
 * the connection is changed.
 */
export function SetupView({
  initial,
  defaultBaseUrl,
  onLoadModels,
  onSave,
  onCancel,
  variant = 'onboarding',
}: SetupViewProps) {
  const [baseUrl, setBaseUrl] = useState(initial?.baseUrl?.trim() || defaultBaseUrl)
  const [virtualKey, setVirtualKey] = useState(initial?.virtualKey ?? '')
  const [models, setModels] = useState<string[]>([])
  const [model, setModel] = useState(initial?.model ?? '')
  const [transcribeModel, setTranscribeModel] = useState(initial?.transcribeModel ?? '')
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState('')

  // Any edit to URL or key invalidates the model list it produced.
  const edit = (set: (value: string) => void) => (value: string) => {
    set(value)
    setModels([])
    setModel('')
    setTranscribeModel('')
    setStatus('idle')
    setError('')
  }

  const connect = async () => {
    setStatus('loading')
    setError('')
    try {
      const list = await onLoadModels(baseUrl.trim(), virtualKey.trim())
      if (list.length === 0) {
        setStatus('error')
        setError('Der Virtual Key ist gültig, hat aber keine Modelle freigeschaltet.')
        return
      }
      setModels(list)
      // Pre-select: the first model that is not a transcription deployment for
      // chat, the first one that looks like one for dictation.
      const chatDefault = list.find((m) => !TRANSCRIBE_HINT.test(m)) ?? list[0] ?? ''
      const sttDefault = list.find((m) => TRANSCRIBE_HINT.test(m)) ?? ''
      setModel((current) => (current && list.includes(current) ? current : chatDefault))
      setTranscribeModel((current) => (current && list.includes(current) ? current : sttDefault))
      setStatus('ready')
    } catch (e) {
      setStatus('error')
      setError(e instanceof Error ? e.message : 'Verbindung fehlgeschlagen.')
    }
  }

  const canConnect = baseUrl.trim().length > 0 && virtualKey.trim().length > 0

  return (
    <div className="flex-1 space-y-6 overflow-y-auto px-4 py-4">
      {variant === 'onboarding' && (
        <div className="flex items-start gap-3 rounded-xl border border-brand-300 bg-brand-50/40 px-3 py-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-100 text-brand-700">
            <KeyRound className="h-4 w-4" />
          </span>
          <div>
            <div className="text-sm font-semibold text-neutral-800">Verbindung einrichten</div>
            <p className="mt-0.5 text-xs text-neutral-600">
              Der Text Assistent arbeitet mit einem LiteLLM-Server. Trage die Server-Adresse und
              deinen Virtual Key ein — danach kannst du dein Modell auswählen.
            </p>
          </div>
        </div>
      )}

      <Section title="LiteLLM-Server">
        <Label>Server-URL</Label>
        <TextInput
          value={baseUrl}
          onChange={(e) => edit(setBaseUrl)(e.target.value)}
          placeholder={defaultBaseUrl}
          autoComplete="off"
          spellCheck={false}
        />
        <Hint>Standard: {defaultBaseUrl}</Hint>

        <div className="mt-3">
          <Label>Virtual Key</Label>
          <TextInput
            type="password"
            value={virtualKey}
            onChange={(e) => edit(setVirtualKey)(e.target.value)}
            placeholder="sk-…"
            autoComplete="off"
            spellCheck={false}
          />
          <Hint>Wird nur lokal auf diesem Gerät gespeichert.</Hint>
        </div>

        <div className="mt-3 flex items-center gap-3">
          <Button
            size="sm"
            onClick={() => void connect()}
            disabled={!canConnect || status === 'loading'}
          >
            {status === 'loading' && <Loader2 className="h-4 w-4 animate-spin" />}
            {status === 'loading' ? 'Verbinde …' : 'Verbinden & Modelle laden'}
          </Button>
          {status === 'ready' && (
            <span className="inline-flex items-center gap-1 text-sm font-medium text-ok">
              <Check className="h-4 w-4" /> {models.length}{' '}
              {models.length === 1 ? 'Modell' : 'Modelle'}
            </span>
          )}
        </div>

        {status === 'error' && (
          <p className="mt-2 inline-flex items-start gap-1.5 text-xs text-red-700">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{error}</span>
          </p>
        )}
      </Section>

      {status === 'ready' && (
        <Section title="Modelle">
          <Label>Chat-Modell</Label>
          <select
            aria-label="Chat-Modell"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className={selectClass}
          >
            {models.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <Hint>Beantwortet alle Anfragen im Chat.</Hint>

          <div className="mt-3">
            <Label>Modell für Spracheingabe</Label>
            <select
              aria-label="Modell für Spracheingabe"
              value={transcribeModel}
              onChange={(e) => setTranscribeModel(e.target.value)}
              className={selectClass}
            >
              <option value="">— keine Spracheingabe —</option>
              {models.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <Hint>
              Wandelt Diktate in Text um (z. B. whisper-1). Ohne Auswahl bleibt das Mikrofon im
              Composer wirkungslos.
            </Hint>
          </div>
        </Section>
      )}

      <div className="flex items-center gap-2 pb-2">
        <Button
          variant="primary"
          disabled={status !== 'ready' || !model}
          onClick={() =>
            onSave({
              baseUrl: baseUrl.trim(),
              virtualKey: virtualKey.trim(),
              model,
              transcribeModel,
            })
          }
        >
          Speichern
        </Button>
        {onCancel && (
          <Button variant="ghost" onClick={onCancel}>
            Abbrechen
          </Button>
        )}
      </div>
    </div>
  )
}

const selectClass =
  'w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-brand-300 focus:outline-none'

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">
        {title}
      </div>
      {children}
    </div>
  )
}
